import React, { useRef, useState } from 'react';
import { Box, Button, ButtonBase, CircularProgress, Popover, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ChromePicker } from 'react-color';
import _ from 'lodash';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import ColorizeOutlinedIcon from '@mui/icons-material/ColorizeOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { useOnboarding } from '../../../contexts/onboarding.context';
import OnbFrame from '../_shared/OnbFrame';
import PhoneFrame from '../_shared/PhoneFrame';
import MockAppHome, { useMockAppPreview } from '../_shared/MockAppHome';
import { COLORS } from '../_shared/tokens';
import { useUser } from '../../../contexts/user.context';
import useActiveTheme from '../../../hooks/activeTheme.hook';
import { mobilizeiStoresService } from '../../../services/mobilizei/stores.service';
import { formatFileName } from '../../../utils/format';
import { showApiError } from '../../../utils/errors';
import mixpanel from 'mixpanel-browser';
import { mixpanelEvents } from '../../../utils/constants';

const PALETTE = ['#000', '#0A4D20', '#3CD568', '#1C93C3', '#D97757', '#B23A6B'];
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];

function useAssetUpload({ onUploaded, platformId, storeId }) {
    const inputRef = useRef(null);
    const [fileName, setFileName] = useState('');
    const [state, setState] = useState('default');

    function handleChange(event) {
        const file = event.target.files[0];

        // The same input serves every attempt — clearing it keeps picking the
        // very same file twice in a row firing a change event.
        event.target.value = '';

        if (!file) {
            return;
        }

        if (!ACCEPTED_TYPES.includes(file.type)) {
            setFileName('');
            setState('error');
            onUploaded('');
            return;
        }

        const body = new FormData();
        body.append('image', file, formatFileName(file.name));

        setFileName(file.name);
        setState('loading');

        mobilizeiStoresService.uploadFile(platformId, storeId, body)
            .then(response => {
                const url = _.get(response, 'fileUrl', '');

                if (!url) {
                    throw new Error('upload sem url');
                }

                onUploaded(url);
                setState('uploaded');
            })
            .catch(error => {
                showApiError(error?.error);
                onUploaded('');
                setState('error');
            });
    }

    return { fileName, handleChange, inputRef, pick: () => inputRef.current?.click(), state };
}


function replaceByKey(original, updated) {
    return original.map(item => updated.find(u => u.key === item.key) || item);
}

function FieldHead({ label }) {
    return (
        <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 0.75 }}>{label}</Typography>
    );
}

/** One asset row (logo / banner) with its thumbnail, status and action. */
function AssetRow({ thumb, title, subtitle, actionLabel, ActionIcon, error, inputRef, loading, uploaded, onAction, onPick }) {
    return (
        <Box
            sx={{
                p: 1.5,
                borderRadius: '10px',
                border: `1px solid ${error ? COLORS.errorFg : uploaded ? COLORS.green : COLORS.border}`,
                backgroundColor: error ? COLORS.errorBg : '#fff',
                display: 'flex',
                alignItems: 'center',
                gap: 1.75
            }}
        >
            <Box
                accept={ACCEPTED_TYPES.join(',')}
                component="input"
                onChange={onPick}
                ref={inputRef}
                sx={{ display: 'none' }}
                type="file"
            />

            {thumb}

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.2, color: error ? COLORS.errorFg : '#000', display: 'flex', alignItems: 'center', gap: 0.875 }}>
                    {/* Uploaded file names are arbitrarily long — clip instead of pushing the row */}
                    <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {title}
                    </Box>
                    {!error && <CheckCircleOutlineIcon sx={{ fontSize: 16, color: COLORS.green, flex: 'none' }} />}
                </Box>
                <Typography sx={{ fontSize: 13, lineHeight: 1.4, color: error ? COLORS.errorFg : COLORS.textMuted, mt: 0.25 }}>
                    {subtitle}
                </Typography>
            </Box>

            <Button
                variant="outlined"
                color="secondary"
                size="small"
                disabled={loading}
                onClick={onAction}
                startIcon={<ActionIcon />}
                sx={{
                    flex: 'none',
                    textTransform: 'none',
                    fontWeight: 500,
                    ...(error && { borderColor: COLORS.errorFg, color: COLORS.errorFg })
                }}
            >
                {actionLabel}
            </Button>
        </Box>
    );
}

/**
 * D02 · Personalize (step 4 of 7). Defaults are already applied, so customising
 * is optional and the primary CTA always moves on. States: default / uploaded / error.
 */
export default function Brand() {
    const { goToNextStep, data, setData, setTheme } = useOnboarding();
    const { user } = useUser();

    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    const [color, setColor] = useState(PALETTE[0]);
    const [customColor, setCustomColor] = useState('')
    const [pickerOpen, setPickerOpen] = useState(false);
    const [updatedCategories, setUpdatedCategories] = useState([])
    const { activeTheme, homeScreenSections, themeSettings } = useActiveTheme();
    const { isHidden: isMockHidden } = useMockAppPreview();

    const [logoChanged, setLogoChanged] = useState(false)
    const [bannerChanged, setBannerChanged] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    const customBtnRef = useRef(null);
    const isCustom = !PALETTE.includes(color);

    // The URLs live in the onboarding data so they survive the reload the
    // platform authorizations cause and the later steps can save them.
    const setAssetUrl = (key) => (url) => {
        if (key === 'logoUrl') {
            setLogoChanged(true)
        } else if (key === 'bannerUrl') {
            setBannerChanged(true)
        }

        setData(previous => ({ ...previous, [key]: url }));
    }

    const logo = useAssetUpload({
        onUploaded: setAssetUrl('logoUrl'),
        platformId: user.store.platformId,
        storeId: user.store.id
    });

    const banner = useAssetUpload({
        onUploaded: setAssetUrl('bannerUrl'),
        platformId: user.store.platformId,
        storeId: user.store.id
    });

    const isError = logo.state === 'error' || banner.state === 'error';
    const isDefault = logo.state === 'default' && banner.state === 'default';

    // While something is wrong or in flight the thumbnail belongs to the status,
    // not to the asset — the URL only takes it over once there is nothing to say.
    const isSettled = (asset) => asset.state !== 'error' && asset.state !== 'loading';
    const showLogoImage = isSettled(logo) && !!data.logoUrl;
    const showBannerImage = isSettled(banner) && !!data.bannerUrl;


    const setPrimaryColor = (c) => {
        setColor(c);
        setData(prev => ({ ...prev, primaryColor: c }));
    }

    const updateTheme = () => {
        const sortingCreatedDesc =
            user.store.platformId === 'tray' || user.store.platformId === 'bagy' ? 'created_desc' :
                user.store.platformId === 'nuvemshop' ? 'created-descending' :
                    user.store.platformId === 'wix' ? 'createdDate-desc' :
                        user.store.platformId === 'irroba' ? 'release-desc' : '';

        const updatedSettings = [
            {
                ...activeTheme.settings.find(s => s.key === 'header'),
                options: {
                    ...activeTheme.settings.find(s => s.key === 'header').options,
                    iconColor: color,
                    logo: {
                        height: 300,
                        width: 420,
                        ...(data.logoUrl && { url: data.logoUrl })
                    }
                }
            },
            {
                ...activeTheme.settings.find(s => s.key === 'drawerMenu'),
                options: {
                    ...activeTheme.settings.find(s => s.key === 'drawerMenu').options,
                    textColor: color,
                    logo: {
                        height: 300,
                        width: 420,
                        ...(data.logoUrl && { url: data.logoUrl })
                    }
                }
            },
            {
                ...activeTheme.settings.find(s => s.key === 'tabBar'),
                options: {
                    ...activeTheme.settings.find(s => s.key === 'tabBar').options,
                    activeIconColor: color
                }
            },
            {
                ...activeTheme.settings.find(s => s.key === 'colors'),
                options: {
                    ...activeTheme.settings.find(s => s.key === 'colors').options,
                    titleColor: color,
                    priceColor: color,
                    buttonColor: color,
                }
            }
        ]

        const updatedAreas = [
            {
                ...activeTheme.areas.find(s => s.key === 'homeScreen'),
                sections: [
                    ...activeTheme.areas.find(s => s.key === 'homeScreen').sections.map((section, index) => {
                        switch (section.key) {
                            case 'banner':
                                return {
                                    ...section,
                                    options: {
                                        ...section.options,
                                        ...(index === 0 && data.bannerUrl && { url: data.bannerUrl })
                                    }
                                }
                            case 'categoriesCarousel':
                                return {
                                    ...section,
                                    options: {
                                        ...section.options,
                                        overlayColor: color
                                    },
                                    content: updatedCategories
                                }
                            case 'productCarousel':
                                return {
                                    ...section,
                                    options: {
                                        ...section.options,
                                        carouselTitleColor: color,
                                        // The first category is the one the store put first;
                                        // a store without categories keeps the theme default.
                                        ...(index === 2 || _.isEmpty(updatedCategories) ?
                                            { defaultSorting: sortingCreatedDesc }
                                            : {
                                                categoryId: updatedCategories[0].categoryId,
                                                productsOrigin: 'category',
                                                carouselTitle: updatedCategories[0].title
                                            })
                                    },
                                }
                            case 'productList':
                                return {
                                    ...section,
                                    options: {
                                        ...section.options,
                                        titleColor: color,
                                    }
                                }
                            case 'socialNetworks':
                                return {
                                    ...section,
                                    options: {
                                        ...section.options,
                                        iconColor: color,
                                        titleColor: color,
                                    }
                                }
                            default:
                                return section;
                        }
                    })
                ]
            },
            {
                ...activeTheme.areas.find(s => s.key === 'categoryScreen'),
                sections: [
                    ...activeTheme.areas.find(s => s.key === 'categoryScreen').sections.map(section => {
                        switch (section.key) {
                            case 'categorySettings':
                                return {
                                    ...section,
                                    options: {
                                        ...section.options,
                                        titleBackgroundColor: color
                                    }
                                }
                            default:
                                return section;
                        }
                    })
                ]
            },
            {
                ...activeTheme.areas.find(s => s.key === 'productDetailsScreen'),
                sections: [
                    ...activeTheme.areas.find(s => s.key === 'productDetailsScreen').sections.map(section => {
                        switch (section.key) {
                            case 'productDetailsSettings':
                                return {
                                    ...section,
                                    options: {
                                        ...section.options,
                                        pagerColor: color
                                    }
                                }
                            default:
                                return section;
                        }
                    })
                ]
            }
        ]

        const colorChanged = color !== PALETTE[0]
        const defaultConfigUsed = !logoChanged && !bannerChanged && !colorChanged

        const updatedTheme = {
            ...activeTheme,
            settings: replaceByKey(activeTheme.settings, updatedSettings),
            areas: replaceByKey(activeTheme.areas, updatedAreas)
        }

        setIsSaving(true);

        mobilizeiStoresService.setTheme(user.store.platformId, user.store.id, user.store.activeTheme, updatedTheme)
            .then(() => {
                setTheme(updatedTheme);
                mixpanel.track(mixpanelEvents.ONBOARDING_CUSTOMIZATION_CONCLUDED, {
                    'platform_id': user.store.platformId,
                    'store_id': user.store.id,
                    'default_config_used': defaultConfigUsed,
                    'color_changed': colorChanged,
                    'logo_changed': logoChanged,
                    'banner_changed': bannerChanged
                })
                goToNextStep();
            })
            .catch(error => {
                showApiError(error?.error);
                setIsSaving(false);
            });
    }

    return (
        <OnbFrame step={4}>
            <Box
                sx={{
                    flex: 1,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: isMockHidden ? '1fr' : '1fr 1fr' },
                    gap: { xs: 4, md: 7 },
                    alignItems: 'center'
                }}
            >
                <Box sx={{ maxWidth: 520, minWidth: 0 }}>
                    <Typography
                        component="h1"
                        sx={{ fontWeight: 700, fontSize: { xs: 26, md: 36 }, lineHeight: 1.1, letterSpacing: '-0.5px', mb: 1.5 }}
                    >
                        Personalize seu app
                    </Typography>

                    <Typography sx={{ fontSize: { xs: 16, md: 17 }, lineHeight: 1.5, color: COLORS.textMuted, mb: 3.25, maxWidth: 480 }}>
                        Seu app já vem pronto com um visual padrão para você começar. Se quiser, personalize o logo, o banner e a cor principal. Depois, você poderá explorar outros temas e um editor completo para deixar tudo do seu jeito.
                    </Typography>

                    {/* Logo */}
                    <Box sx={{ mb: 2 }}>
                        <FieldHead label="Logo" />
                        <AssetRow
                            error={logo.state === 'error'}
                            inputRef={logo.inputRef}
                            loading={logo.state === 'loading'}
                            uploaded={logo.state === 'uploaded'}
                            onAction={logo.pick}
                            onPick={logo.handleChange}
                            thumb={
                                <Box
                                    sx={{
                                        width: 56,
                                        height: 40,
                                        borderRadius: '10px',
                                        flex: 'none',
                                        overflow: 'hidden',
                                        background: banner.state === 'error' ? '#fff' : COLORS.surfaceMuted,
                                        color: logo.state === 'error' ? COLORS.errorFg : '#fff',
                                        border: logo.state === 'error' ? `1px solid ${COLORS.errorFg}` : 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 700,
                                        fontSize: 18
                                    }}
                                >
                                    {logo.state === 'error' ? <BrokenImageOutlinedIcon sx={{ fontSize: 24 }} />
                                        : logo.state === 'loading' ? <CircularProgress size={18} sx={{ color: '#000' }} />
                                            : showLogoImage
                                                ? <Box component="img" alt="logo" src={data.logoUrl} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                : data.storeName[0]}
                                </Box>
                            }
                            title={
                                logo.state === 'error' ? 'Não foi possível enviar'
                                    : logo.fileName || 'Logo padrão em uso'
                            }
                            subtitle={
                                logo.state === 'error' ? 'Use PNG, JPG ou SVG · recomendado 420×300px'
                                    : logo.state === 'loading' ? 'enviando…'
                                        : logo.state === 'uploaded' ? 'enviado agora'
                                            : 'recomendado: 420×300px'
                            }
                            actionLabel={
                                logo.state === 'error' ? 'tentar outro'
                                    : logo.state === 'loading' ? 'enviando'
                                        : logo.state === 'uploaded' ? 'trocar'
                                            : 'enviar'
                            }
                            ActionIcon={
                                logo.state === 'error' ? RefreshOutlinedIcon
                                    : logo.state === 'uploaded' ? SyncOutlinedIcon
                                        : UploadOutlinedIcon
                            }
                        />
                    </Box>

                    {/* Banner */}
                    <Box sx={{ mb: 2 }}>
                        <FieldHead label="Banner inicial" />
                        <AssetRow
                            error={banner.state === 'error'}
                            inputRef={banner.inputRef}
                            loading={banner.state === 'loading'}
                            uploaded={banner.state === 'uploaded'}
                            onAction={banner.pick}
                            onPick={banner.handleChange}
                            thumb={
                                <Box
                                    sx={{
                                        width: 90,
                                        height: 54,
                                        borderRadius: '8px',
                                        flex: 'none',
                                        overflow: 'hidden',
                                        background: banner.state === 'error' ? '#fff' : COLORS.surfaceMuted,
                                        border: banner.state === 'error' ? `1px solid ${COLORS.errorFg}` : 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        p: 0,
                                        color: banner.state === 'error' ? COLORS.errorFg : '#fff',
                                        fontWeight: 700,
                                        fontSize: 10,
                                        lineHeight: 1.1
                                    }}
                                >
                                    {banner.state === 'error' ? <BrokenImageOutlinedIcon sx={{ fontSize: 24 }} />
                                        : banner.state === 'loading' ? <CircularProgress size={18} sx={{ color: '#000' }} />
                                            : showBannerImage
                                                ? <Box component="img" alt="banner" src={data.bannerUrl} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                : <>Coleção<br />verão</>}
                                </Box>
                            }
                            title={
                                banner.state === 'error' ? 'Não foi possível enviar'
                                    : banner.fileName || 'Banner padrão em uso'
                            }
                            subtitle={
                                banner.state === 'error' ? 'Use PNG, JPG ou SVG · recomendado 800×480px'
                                    : banner.state === 'loading' ? 'enviando…'
                                        : banner.state === 'uploaded' ? 'enviado agora'
                                            : 'recomendado: 800×480px'
                            }
                            actionLabel={
                                banner.state === 'error' ? 'tentar outro'
                                    : banner.state === 'loading' ? 'enviando'
                                        : banner.state === 'uploaded' ? 'trocar'
                                            : 'enviar'
                            }
                            ActionIcon={
                                banner.state === 'error' ? RefreshOutlinedIcon
                                    : banner.state === 'uploaded' ? SyncOutlinedIcon
                                        : UploadOutlinedIcon
                            }
                        />
                    </Box>

                    {/* Primary color */}
                    <Box sx={{ mb: 3.25 }}>
                        <FieldHead label="Cor principal" />
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {PALETTE.map((c) => (
                                <ButtonBase
                                    key={c}
                                    onClick={() => setPrimaryColor(c)}
                                    aria-label={`cor ${c}`}
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: '10px',
                                        backgroundColor: c,
                                        boxShadow: color === c ? `0 0 0 2px #fff, 0 0 0 4px ${COLORS.green}` : 'none'
                                    }}
                                >
                                    {color === c && <CheckIcon sx={{ fontSize: 20, color: '#fff' }} />}
                                </ButtonBase>
                            ))}

                            <ButtonBase
                                ref={customBtnRef}
                                onClick={() => {
                                    setPickerOpen(true)
                                    if (customColor) {
                                        setPrimaryColor(customColor)
                                    }
                                }}
                                aria-label="cor personalizada"
                                sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: '10px',
                                    ...(isCustom
                                        ? {
                                            color: '#fff',
                                            boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${COLORS.green}`
                                        }
                                        : {
                                            border: `1px dashed ${COLORS.border}`,
                                            color: COLORS.textMuted
                                        }),
                                    ...(customColor &&
                                    {
                                        backgroundColor: customColor,
                                    }
                                    )
                                }}
                            >
                                {isCustom
                                    ? <CheckIcon sx={{ fontSize: 20, color: '#fff' }} />
                                    : <ColorizeOutlinedIcon sx={{ fontSize: 20, color: customColor ? '#fff' : '#000' }} />}
                            </ButtonBase>

                            <Popover
                                open={pickerOpen}
                                anchorEl={customBtnRef.current}
                                onClose={() => setPickerOpen(false)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                slotProps={{ paper: { sx: { mt: 1, borderRadius: '10px', overflow: 'hidden' } } }}
                            >
                                <ChromePicker
                                    color={customColor}
                                    disableAlpha
                                    onChange={(c) => {
                                        setPrimaryColor(c.hex)
                                        setCustomColor(c.hex)
                                    }} />
                            </Popover>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'stretch', md: 'center' }, gap: { xs: 1, md: 2.25 } }}>
                        <Button
                            variant="contained"
                            disabled={isSaving}
                            onClick={updateTheme}
                            endIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIcon />}
                            sx={{ flex: 'none', py: { xs: 1.75, md: 1.5 }, opacity: isError ? 0.5 : 1 }}
                        >
                            {isSaving ? 'salvando…' : 'continuar'}
                        </Button>

                        {isDefault && (
                            <Typography sx={{ fontSize: 14, lineHeight: 1.4, color: COLORS.textMuted, maxWidth: 190 }}>
                                os padrões já estão aplicados
                            </Typography>
                        )}
                    </Box>

                    {isError && (
                        <Typography sx={{ mt: 1.75, fontSize: 14, lineHeight: 1.4, color: COLORS.errorFg, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                            Envie uma imagem válida ou siga com o padrão.
                        </Typography>
                    )}
                </Box>

                {/* Live preview — a smaller device on mobile, where it sits below
                    the form instead of in a side column. */}
                {!isMockHidden && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            position: 'relative',
                            minWidth: 0,
                            backgroundColor: COLORS.surfaceMuted,
                            borderRadius: '16px',
                            px: { xs: 2, md: 4 },
                            py: { xs: 2.5, md: 3.5 }
                        }}
                    >
                        <Box sx={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: COLORS.green }} />
                            <Typography
                                sx={{ fontWeight: 700, fontSize: 12, letterSpacing: '1px', textTransform: 'uppercase', color: COLORS.textMuted }}
                            >
                                preview ao vivo
                            </Typography>
                        </Box>

                        <PhoneFrame scale={isDesktop ? 0.92 : 0.5} sx={{ position: 'relative' }}>
                            <MockAppHome
                                bannerUrl={data.bannerUrl}
                                logoUrl={data.logoUrl}
                                storeName={data.storeName}
                                primaryColor={color}
                                homeScreenSections={homeScreenSections}
                                themeSettings={themeSettings}
                                setUpdatedCategories={setUpdatedCategories}
                            />
                        </PhoneFrame>

                        <Typography sx={{ mt: 2.5, fontSize: 14, lineHeight: 1.5, color: COLORS.textMuted, textAlign: 'center', maxWidth: 320 }}>
                            O aplicativo da {data.storeName} já está assim. Seus produtos de verdade, sem você enviar nada.
                        </Typography>
                    </Box>
                )}
            </Box>
        </OnbFrame>
    );
}
